import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { I18nProvider } from '@lingui/react';
import { useLingui } from '@lingui/react/macro';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { useColorScheme } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
// One import per supported UI language (see `supportedLocales`) — the pickers' adapterLocale
// follows i18n.locale and dayjs silently falls back to English for unregistered locales.
import 'dayjs/locale/fr';
import 'dayjs/locale/es';
import 'dayjs/locale/ru';
import 'dayjs/locale/zh';
import 'dayjs/locale/ja';
import 'dayjs/locale/ko';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { type ReactNode, useEffect } from 'react';
import { setBlockExplorerUrl } from '@/common/blockExplorer';
import { muiTheme } from '@/theme';
import 'ui-kit/global.css';

import { ErrorBoundary } from '@/components/error_boundary.tsx';
import { AnnouncementsContainer } from '@/feature/announcements';
import { NotificationsContainer } from '@/feature/notifications';
import { VersionUpdateToast } from '../components/VersionUpdateToast';
import { useAnnouncementsPolling } from '../hooks/useAnnouncementsPolling';
import { useGeoBlockAnnouncement } from '../hooks/useGeoBlockAnnouncement';
import { i18n } from '../locales/i18n';
import useAppStore from '../store/rootStore.ts';
import { telemetry } from '../utils/telemetry';
// Side-effect import: registers the 'switchToMarket' toast action handler.
import '../features/account/liquidationAlertsRegistration';

export const EMOTION_CACHE_SELECTOR = 'emotion-insertion-point';

// Create emotion cache internally
const emotionInsertionPoint = document.querySelector<HTMLMetaElement>(`meta[name="${EMOTION_CACHE_SELECTOR}"]`);
const cache = createCache({
  key: 'my-app',
  insertionPoint: emotionInsertionPoint || undefined,
});

export const UIProviders = ({ children }: { children: ReactNode }) => {
  const explorerUrl = useAppStore((state) => state.config?.chain?.blockExplorers?.default?.url);
  const analyticsEnabled = useAppStore((state) => state.config?.features?.analyticsEnabled);

  useEffect(() => {
    setBlockExplorerUrl(explorerUrl);
  }, [explorerUrl]);

  useAnnouncementsPolling();

  const content = (
    // Changed modeStorageKey to mui-color-mode-v2 to avoid conflicts with the old modeStorageKey and keep dark mode
    <ThemeProvider theme={muiTheme} defaultMode="dark" modeStorageKey="mui-color-mode-v2">
      <I18nProvider i18n={i18n}>
        <CssBaseline />
        <CacheProvider value={cache}>
          <LocaleAwareLocalizationProvider>
            <ThemeTelemetryTracker />
            {children}
            <ErrorBoundary message="Can't load notifications. Refresh page.">
              <NotificationsContainer />
              <AppAnnouncements />
              <VersionUpdateToast />
            </ErrorBoundary>
          </LocaleAwareLocalizationProvider>
        </CacheProvider>
      </I18nProvider>
    </ThemeProvider>
  );

  if (!analyticsEnabled) {
    return content;
  }

  return <PostHogProvider client={posthog}>{content}</PostHogProvider>;
};

function LocaleAwareLocalizationProvider({ children }: { children: ReactNode }) {
  const { i18n } = useLingui();
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.locale}>
      {children}
    </LocalizationProvider>
  );
}

// Below I18nProvider, unlike the UIProviders body, so the hooks can translate.
const AppAnnouncements = () => {
  const geoBlockNotice = useGeoBlockAnnouncement();
  return <AnnouncementsContainer extraAnnouncements={geoBlockNotice ? [geoBlockNotice] : []} />;
};

const ThemeTelemetryTracker = () => {
  const { mode } = useColorScheme();
  useEffect(() => {
    if (mode === 'dark' || mode === 'light') telemetry.setTheme(mode);
  }, [mode]);
  return null;
};
